import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { ComponentsCustomerType } from "@omni/types/components/customer/shared/customerType.enum";
import { cn } from "@omni/utils/styles";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

import { StaticAddress } from "@/components/Checkout/CheckoutCustomer/addresses/components/staticAddress/staticAddress";
import { useAddresses } from "@/components/Checkout/CheckoutCustomer/addresses/hooks/useAddresses.hook";
import { CheckoutCustomerFormValues } from "@/components/Checkout/CheckoutCustomer/checkoutCustomer.schema";
import { CustomerAddressFormBlock } from "@/components/Form/RHF/FormBlocks/Address/addressFormBlock.container";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import * as CheckoutCustomerDetailsFormSelectors from "@/redux/modules/value/checkout/customerDetails/checkoutCustomerDetailsForm.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type Props = {
    className?: string;
};

export const PaymentAddress = ({ className }: Props) => {
    const { t } = useGranite();

    const { addresses } = useAddresses();

    const checkoutCustomerDetailsForm = useSelector(
        CheckoutCustomerDetailsFormSelectors.getCheckoutCustomerDetailsForm
    );
    const isHomeDelivery = useSelector(CartSelectors.hasHomeDelivery);
    const isCnCOnly = useSelector(CartSelectors.isClickAndCollectOnly);

    const { customerType, samePaymentAddress, requestInvoice } =
        useWatch<CheckoutCustomerFormValues>();

    const isB2B = customerType === ComponentsCustomerType.B2B;

    const paymentAddressId = checkoutCustomerDetailsForm?.paymentAddressId;
    const paymentAddress = addresses.find(
        ({ id }) => id === paymentAddressId && !!id
    );

    const showPaymentAddressSection = useMemo(() => {
        if (isCnCOnly) {
            return requestInvoice || isB2B;
        }

        if (isHomeDelivery) {
            return !samePaymentAddress;
        }
    }, [isHomeDelivery, samePaymentAddress, isCnCOnly, requestInvoice, isB2B]);

    if (!showPaymentAddressSection) return null;

    return (
        <section className={cn(className)}>
            <h2>{t("checkout.customerDetails.paymentAddress.title")}</h2>
            <hr />
            <p>
                <i>{t("checkout.customerDetails.paymentAddress.subtitle")}</i>
            </p>
            {!!paymentAddress && (
                <StaticAddress
                    address={paymentAddress}
                    addressType={AddressType.Payment}
                />
            )}
            {!paymentAddress && (
                <CustomerAddressFormBlock<
                    CheckoutCustomerFormValues,
                    AddressType.Payment
                >
                    addressType={AddressType.Payment}
                    addressStatus={AddressValidationStatus.Unknown}
                    skipValidation={true}
                />
            )}
        </section>
    );
};
